import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Button, GradientText } from '@/design-system'
import { LandingNav } from './landing-v2/LandingNav'
import { useAuth } from '@/hooks/common'
import { startCheckout } from '@/lib/payments'
import { fireAlertToast } from '@/design-system/alerts'
import { APP_NAME } from '@/utils/constants'
import { LogoMark } from '@/features/album/components/EgowallLogo'

/** Gallery ($9/mo·$90/yr) and Studio ($24/mo·$240/yr) map to payments-infra's
 * generic 'pro' / 'enterprise' tier names — see src/lib/payments.ts. Keepsake
 * is free, so it never calls checkout. */
const PLAN_TIER: Record<string, 'pro' | 'enterprise'> = { gallery: 'pro', studio: 'enterprise' }

/* ── layout ──────────────────────────────────────────────────────────── */

const Page = styled(Box)({
  background: 'var(--ds-surface)',
  color: 'var(--ds-ink)',
  minHeight: '100vh',
  fontFamily: 'var(--ds-font)',
})

/** Matches the mockup's `.wrap` — 1140px centered with horizontal padding. */
const Wrap = styled(Box)({
  maxWidth: 1140,
  margin: '0 auto',
  padding: '0 32px',
  '@media (max-width: 600px)': { padding: '0 20px' },
})

/* ── hero ─────────────────────────────────────────────────────────────── */

const PHero = styled('header')({
  textAlign: 'center',
  /** 62px nav + 84px design spec top padding */
  padding: '146px 32px 40px',
  position: 'relative',
  overflow: 'hidden',
})

const HeroGlow = styled(Box)({
  position: 'absolute',
  top: -260,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 1000,
  height: 560,
  background:
    'radial-gradient(ellipse 50% 50% at 50% 50%, rgba(255,207,125,0.13), rgba(190,157,253,0.1) 50%, transparent 75%)',
  pointerEvents: 'none',
})

const D2 = styled('h1')({
  fontFamily: 'var(--ds-font)',
  fontSize: 'clamp(36px, 5.5vw, 60px)',
  fontWeight: 760,
  letterSpacing: '-0.04em',
  lineHeight: 1.05,
  marginTop: 14,
  marginBottom: 0,
  color: 'var(--ds-ink)',
})

const BodyLg = styled('p')({
  fontFamily: 'var(--ds-font)',
  fontSize: 17,
  color: 'var(--ds-ink-muted)',
  lineHeight: 1.55,
  margin: '18px auto 0',
  maxWidth: 480,
})

/* ── billing toggle ───────────────────────────────────────────────────── */

const BillToggle = styled(Box)({
  margin: '30px auto 0',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 12,
})

const BillLabel = styled('span', {
  shouldForwardProp: (p) => p !== '$on',
})<{ $on?: boolean }>(({ $on }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: $on ? 'var(--ds-ink)' : 'var(--ds-ink-subtle)',
  transition: 'color 0.15s ease',
  cursor: 'pointer',
  userSelect: 'none',
}))

const Toggle = styled(Box, {
  shouldForwardProp: (p) => p !== '$yearly',
})<{ $yearly?: boolean }>(({ $yearly }) => ({
  width: 46,
  height: 26,
  borderRadius: 13,
  background: 'var(--ds-ink)',
  position: 'relative',
  cursor: 'pointer',
  flexShrink: 0,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 3,
    left: 3,
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#fff',
    transition: 'transform 0.22s ease',
    transform: $yearly ? 'translateX(20px)' : 'none',
  },
}))

const SaveTag = styled(Box)({
  display: 'inline-block',
  fontFamily: 'var(--ds-font-mono, "IBM Plex Mono", monospace)',
  fontSize: 9.5,
  letterSpacing: '0.1em',
  padding: '4px 9px',
  borderRadius: 'var(--ds-radius-pill)',
  background:
    'var(--ds-gradient-spectrum-soft, color-mix(in srgb, var(--ds-accent) 8%, var(--ds-canvas)))',
  color: 'var(--ds-accent-deep-b, var(--ds-accent))',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  lineHeight: 1,
})

/* ── plan cards ───────────────────────────────────────────────────────── */

const Plans = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 20,
  padding: '44px 0 20px',
  alignItems: 'stretch',
  '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
})

const PlanCard = styled(Box, {
  shouldForwardProp: (p) => p !== '$featured',
})<{ $featured?: boolean }>(({ $featured }) => ({
  position: 'relative',
  background: 'var(--ds-canvas)',
  borderRadius: 'var(--ds-radius-xl)',
  boxShadow: $featured ? 'var(--ds-shadow-lift)' : 'var(--ds-shadow-soft)',
  padding: '30px 28px',
  display: 'flex',
  flexDirection: 'column',
  ...($featured && {
    border: '2.5px solid transparent',
    backgroundImage: `linear-gradient(var(--ds-canvas), var(--ds-canvas)) padding-box,
       var(--ds-gradient-spectrum) border-box`,
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
  }),
}))

const PopTag = styled(Box)({
  position: 'absolute',
  top: -13,
  left: '50%',
  transform: 'translateX(-50%)',
  height: 26,
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0 14px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'var(--ds-ink)',
  color: '#fff',
  fontFamily: 'var(--ds-font-mono, "IBM Plex Mono", monospace)',
  fontSize: 9.5,
  letterSpacing: '0.12em',
  fontWeight: 600,
  whiteSpace: 'nowrap',
})

const PlanName = styled('div')({
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: '-0.01em',
  color: 'var(--ds-ink)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const PlanFor = styled('div')({
  fontSize: 12.5,
  color: 'var(--ds-ink-subtle)',
  marginTop: 3,
})

const PriceRow = styled(Box)({
  marginTop: 22,
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
})

const PriceVal = styled('span')({
  fontSize: 44,
  fontWeight: 760,
  letterSpacing: '-0.045em',
  lineHeight: 1,
  color: 'var(--ds-ink)',
})

const PricePer = styled('span')({
  fontSize: 13,
  color: 'var(--ds-ink-subtle)',
})

const FeatList = styled(Box)({
  marginTop: 24,
  display: 'flex',
  flexDirection: 'column',
})

const Feat = styled(Box, {
  shouldForwardProp: (p) => p !== '$dim',
})<{ $dim?: boolean }>(({ $dim }) => ({
  display: 'flex',
  gap: 10,
  padding: '8px 0',
  fontSize: 13.5,
  color: $dim ? 'var(--ds-ink-subtle)' : 'var(--ds-ink-muted)',
  '& .c': {
    color: $dim
      ? 'var(--ds-border-strong)'
      : 'var(--ds-accent-deep-a, #e97a4a)',
    fontWeight: 700,
    flexShrink: 0,
  },
  '& b': { color: 'var(--ds-ink)', fontWeight: 640 },
}))

/* ── compare table ────────────────────────────────────────────────────── */

const CmpTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  background: 'var(--ds-canvas)',
  borderRadius: 'var(--ds-radius-lg)',
  overflow: 'hidden',
  boxShadow: 'var(--ds-shadow-soft)',
  '& th, & td': {
    padding: '14px 18px',
    textAlign: 'center',
    fontSize: 13,
    borderBottom: '1px solid var(--ds-border)',
  },
  '& th': {
    fontFamily: 'var(--ds-font-mono, "IBM Plex Mono", monospace)',
    fontSize: 10,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--ds-ink-subtle)',
    fontWeight: 500,
    background: 'var(--ds-surface)',
  },
  '& td:first-of-type, & th:first-of-type': {
    textAlign: 'left',
    fontWeight: 600,
    color: 'var(--ds-ink)',
  },
  '& tr:last-child td': { borderBottom: 'none' },
  '& .hl': { background: 'rgba(190,157,253,0.05)' },
  '& .y': { color: 'var(--ds-accent-deep-a, #e97a4a)', fontWeight: 700 },
  '& .n': { color: 'var(--ds-border-strong)' },
})

/* ── faq ──────────────────────────────────────────────────────────────── */

const FaqItem = styled('details')({
  borderBottom: '1px solid var(--ds-border)',
  '& summary': {
    listStyle: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 4px',
    fontSize: 15,
    fontWeight: 640,
    letterSpacing: '-0.01em',
    color: 'var(--ds-ink)',
    userSelect: 'none',
    '&::-webkit-details-marker': { display: 'none' },
  },
  '& .pm': {
    fontSize: 18,
    color: 'var(--ds-ink-subtle)',
    transition: 'transform 0.2s ease',
    flexShrink: 0,
    marginLeft: 16,
  },
  '&[open] .pm': { transform: 'rotate(45deg)' },
  '& .a': {
    padding: '0 4px 22px',
    fontSize: 14,
    lineHeight: 1.65,
    color: 'var(--ds-ink-muted)',
    maxWidth: 620,
  },
})

/* ── footer ───────────────────────────────────────────────────────────── */

const Foot = styled('footer')({
  background: 'var(--ds-night-canvas)',
  color: 'var(--ds-night-ink)',
  borderTop: '1px solid var(--ds-night-border)',
})

const FootInner = styled(Box)({
  maxWidth: 1240,
  margin: '0 auto',
  padding: '56px clamp(20px, 4vw, 48px) 34px',
})

const FootCols = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '2fr 1fr 1fr 1fr',
  gap: 36,
  paddingBottom: 44,
  '@media (max-width: 800px)': { gridTemplateColumns: '1fr 1fr' },
})

const ColTitle = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.35))',
  marginBottom: 13,
})

const FootLink = styled(RouterLink)({
  display: 'block',
  padding: '4px 0',
  fontSize: 13,
  color: 'var(--ds-night-ink-muted, rgba(244,243,246,0.55))',
  textDecoration: 'none',
  transition: 'color 0.15s ease',
  '&:hover': { color: 'var(--ds-night-ink)' },
})

const Wordmark = styled(Box)({
  fontSize: 'clamp(70px, 13vw, 170px)',
  fontWeight: 800,
  letterSpacing: '-0.055em',
  lineHeight: 0.85,
  paddingBottom: '0.3em',
  background: 'var(--ds-gradient-spectrum)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  opacity: 0.92,
  userSelect: 'none',
})

const Legal = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 14,
  paddingTop: 22,
  borderTop: '1px solid var(--ds-night-border, rgba(244,243,246,0.08))',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.12em',
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.35))',
  flexWrap: 'wrap',
})

/* ── data ─────────────────────────────────────────────────────────────── */

const PLANS = [
  {
    id: 'keepsake',
    name: 'Keepsake',
    badge: null,
    forLine: 'For your first albums',
    monthlyPrice: '$0',
    yearlyPrice: '$0',
    pricePer: 'forever',
    ctaLabel: 'Start free',
    ctaVariant: 'secondary' as const,
    featured: false,
    features: [
      { text: <><b>3 albums</b>, unlimited viewers</>, dim: false },
      { text: <><b>Full resolution</b> — always, even free</>, dim: false },
      { text: <>Reactions, comments &amp; pins</>, dim: false },
      { text: <>1 share key per album + QR</>, dim: false },
      { text: <>Guest uploads</>, dim: true },
      { text: <>3D galleries</>, dim: true },
    ],
  },
  {
    id: 'gallery',
    name: 'Gallery',
    badge: '3D INCLUDED',
    forLine: 'For weddings, events & family archives',
    monthlyPrice: '$9',
    yearlyPrice: '$7.50',
    pricePer: '/ month',
    ctaLabel: 'Start 14-day trial →',
    ctaVariant: 'spectrum' as const,
    featured: true,
    features: [
      { text: <><b>Unlimited albums</b> &amp; unlimited keys</>, dim: false },
      { text: <><b>3D walkable galleries</b> — 14 Classic scenes + Studio presets</>, dim: false },
      { text: <>Reactions, comments &amp; pins</>, dim: false },
      { text: <>Share links + QR</>, dim: false },
      { text: <>Guest uploads</>, dim: true },
      { text: <>Voice notes &amp; anniversary reopen</>, dim: true },
    ],
  },
  {
    id: 'studio',
    name: 'Studio',
    badge: null,
    forLine: 'For photographers & event pros',
    monthlyPrice: '$24',
    yearlyPrice: '$20',
    pricePer: '/ month',
    ctaLabel: 'Start trial',
    ctaVariant: 'secondary' as const,
    featured: false,
    features: [
      { text: <>Everything in <b>Gallery</b></>, dim: false },
      { text: <>Priority support</>, dim: false },
      { text: <><b>Client workspaces</b> &amp; branding</>, dim: true },
      { text: <>RAW proofing &amp; downloads</>, dim: true },
      { text: <>Analytics per key</>, dim: true },
    ],
  },
]

const COMPARE_ROWS: { feature: string; keepsake: boolean | string; gallery: boolean | string; studio: boolean | string }[] = [
  { feature: 'Albums',                       keepsake: '3',           gallery: 'Unlimited',            studio: 'Unlimited' },
  { feature: 'Resolution',                   keepsake: 'Full',        gallery: 'Full',                 studio: 'Full' },
  { feature: 'Reactions, comments, pins',    keepsake: true,          gallery: true,                   studio: true },
  { feature: 'Share keys + QR',             keepsake: '1 / album',   gallery: 'Unlimited, revocable', studio: 'Unlimited, revocable' },
  { feature: '3D walkable galleries',       keepsake: false,         gallery: '✓ 14 Classic + Studio', studio: '✓ 14 Classic + Studio' },
  { feature: 'Guest uploads',               keepsake: false,         gallery: 'Soon',                 studio: 'Soon' },
  { feature: 'Voice notes & presence',      keepsake: false,         gallery: 'Soon',                 studio: 'Soon' },
  { feature: 'Client workspaces & branding',keepsake: false,         gallery: false,                  studio: 'Soon' },
  { feature: 'RAW proofing & analytics',    keepsake: false,         gallery: false,                  studio: 'Soon' },
]

const FAQS = [
  {
    q: 'Is "full resolution" really full resolution on the free plan?',
    a: 'Yes. Fidelity is the product — we will never hold your pixels hostage. Free plans are limited by album count, not by quality or by a storage meter.',
  },
  {
    q: 'Do my guests need an account?',
    a: 'Never. A key (link or QR) opens the album on any device. To react or comment, guests just type a first name. Hosts can revoke any key at any time.',
  },
  {
    q: 'What happens to my albums if I cancel?',
    a: 'They stay viewable forever, at full resolution, for you and everyone with a key. You lose the ability to create new albums beyond the free tier, not the memories you already made.',
  },
  {
    q: 'What exactly is a 3D gallery?',
    a: 'A real, walkable room rendered in your browser — your photos framed and hung on the walls. You pick a Classic scene or Studio preset, frames, and finishes; guests open the same share link and walk through. No downloads, no 3D skills.',
  },
  {
    q: 'Can guests add their own photos?',
    a: 'Not yet — guest uploads are on the roadmap for Gallery and Studio. Today guests can view, react, and comment through a key; hosts upload and arrange the album.',
  },
]

function CellVal({ val }: { val: boolean | string }) {
  if (val === true) return <span className='y'>✓</span>
  if (val === false) return <span className='n'>—</span>
  if (typeof val === 'string' && val.startsWith('✓'))
    return <span><span className='y'>✓</span>{val.slice(1)}</span>
  return <>{val}</>
}

/* ── page ─────────────────────────────────────────────────────────────── */

export function PricingPage() {
  const [yearly, setYearly] = useState(false)
  const [checkingOutId, setCheckingOutId] = useState<string | null>(null)
  const navigate = useNavigate()
  const { user } = useAuth()

  const handlePlanClick = async (planId: string) => {
    const tier = PLAN_TIER[planId]

    // Keepsake (free) never checks out — same signup CTA as before.
    if (!tier) {
      navigate('/auth?step=signup')
      return
    }

    // No session yet — send them to sign up, then back here to actually
    // check out once they have an account. (Auto-resuming checkout right
    // after signup is a reasonable fast-follow, not done here.)
    if (!user?.userID) {
      navigate(`/auth?step=signup&redirect=${encodeURIComponent('/pricing')}`)
      return
    }

    setCheckingOutId(planId)
    try {
      await startCheckout(user.userID, user.userName, tier, yearly ? 'yearly' : 'monthly')
      // startCheckout redirects to Stripe on success — nothing left to do here.
    } catch {
      fireAlertToast('settings.checkoutFailed')
      setCheckingOutId(null)
    }
  }

  return (
    <Page>
      <LandingNav />

      {/* ── hero ── */}
      <PHero>
        <HeroGlow />
        <Box sx={{ position: 'relative' }}>
          <GradientText variant='eyebrow'>Pricing</GradientText>
          <D2>Pay for moments,<br />not megabytes.</D2>
          <BodyLg>
            Every plan keeps your photos at full resolution. You upgrade for rooms, keys, and the
            people you let in — never for storage anxiety.
          </BodyLg>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <BillToggle>
              <BillLabel $on={!yearly} onClick={() => setYearly(false)}>Monthly</BillLabel>
              <Toggle $yearly={yearly} onClick={() => setYearly((v) => !v)} role='switch' aria-checked={yearly} />
              <BillLabel $on={yearly} onClick={() => setYearly(true)}>Yearly</BillLabel>
              <SaveTag>2 MONTHS FREE</SaveTag>
            </BillToggle>
          </Box>
        </Box>
      </PHero>

      {/* ── plan cards ── */}
      <Wrap>
        <Plans>
          {PLANS.map((plan) => (
            <PlanCard key={plan.id} $featured={plan.featured}>
              {plan.featured && <PopTag>MOST POPULAR</PopTag>}
              <PlanName>
                {plan.name}
                {plan.badge && <SaveTag sx={{ fontSize: '8.5px !important' }}>{plan.badge}</SaveTag>}
              </PlanName>
              <PlanFor>{plan.forLine}</PlanFor>
              <PriceRow>
                <PriceVal>
                  {yearly && plan.yearlyPrice !== plan.monthlyPrice ? plan.yearlyPrice : plan.monthlyPrice}
                </PriceVal>
                <PricePer>{plan.pricePer}</PricePer>
              </PriceRow>
              <Box sx={{ mt: '20px' }}>
                <Button
                  variant={plan.ctaVariant}
                  size='sm'
                  fullWidth
                  loading={checkingOutId === plan.id}
                  disabled={checkingOutId !== null && checkingOutId !== plan.id}
                  onClick={() => handlePlanClick(plan.id)}
                >
                  {plan.ctaLabel}
                </Button>
              </Box>
              <FeatList>
                {plan.features.map((f, i) => (
                  <Feat key={i} $dim={f.dim}>
                    <span className='c'>{f.dim ? '—' : '✓'}</span>
                    <span>{f.text}</span>
                  </Feat>
                ))}
              </FeatList>
            </PlanCard>
          ))}
        </Plans>

        <Typography sx={{ textAlign: 'center', padding: '12px 0 40px', fontSize: 12, color: 'var(--ds-ink-subtle)' }}>
          All prices USD. Yearly billing shown as effective monthly rate. Cancel anytime — albums stay viewable forever.
        </Typography>

        {/* ── compare ── */}
        <Box component='section' sx={{ padding: '50px 0 80px' }}>
          <Typography sx={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 740, letterSpacing: '-0.03em', textAlign: 'center', mb: '34px' }}>
            Compare plans
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <CmpTable>
              <thead>
                <tr>
                  <th>Feature</th><th>Keepsake</th><th className='hl'>Gallery</th><th>Studio</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row) => (
                  <tr key={row.feature}>
                    <td>{row.feature}</td>
                    <td><CellVal val={row.keepsake} /></td>
                    <td className='hl'><CellVal val={row.gallery} /></td>
                    <td><CellVal val={row.studio} /></td>
                  </tr>
                ))}
              </tbody>
            </CmpTable>
          </Box>
        </Box>

        {/* ── faq ── */}
        <Box component='section' sx={{ pb: '100px' }}>
          <Typography sx={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 740, letterSpacing: '-0.03em', textAlign: 'center', mb: '20px' }}>
            Questions, answered
          </Typography>
          <Box sx={{ maxWidth: 720, margin: '0 auto' }}>
            {FAQS.map((faq) => (
              <FaqItem key={faq.q}>
                <summary>{faq.q}<span className='pm'>+</span></summary>
                <div className='a'>{faq.a}</div>
              </FaqItem>
            ))}
          </Box>
        </Box>
      </Wrap>

      {/* ── footer ── */}
      <Foot>
        <FootInner>
          <FootCols>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 760, color: 'var(--ds-night-ink)', fontSize: 16 }}>
                <LogoMark />
                {APP_NAME}
              </Box>
              <Box sx={{ mt: '14px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.35))', maxWidth: 230 }}>
                Photo albums people actually visit. Full resolution, real conversation, one key away.
              </Box>
            </Box>
            <Box>
              <ColTitle>Product</ColTitle>
              <FootLink to='/#fidelity'>Fidelity</FootLink>
              <FootLink to='/#interactions'>Interactions</FootLink>
              <FootLink to='/#key'>The Key</FootLink>
              <FootLink to='/#room'>3D Galleries</FootLink>
              <FootLink to='/pricing' style={{ color: 'var(--ds-night-ink)' }}>Pricing</FootLink>
            </Box>
            <Box>
              <ColTitle>Company</ColTitle>
              <FootLink to='/'>About</FootLink>
              <FootLink to='/'>Journal</FootLink>
              <FootLink to='/'>Careers</FootLink>
              <FootLink to='/'>Press kit</FootLink>
            </Box>
            <Box>
              <ColTitle>Support</ColTitle>
              <FootLink to='/'>Help center</FootLink>
              <FootLink to='/privacy'>Privacy</FootLink>
              <FootLink to='/terms'>Terms</FootLink>
              <FootLink to='/'>Status</FootLink>
            </Box>
          </FootCols>
          <Wordmark>{APP_NAME}</Wordmark>
          <Legal>
            <span>© 2026 EGOWALL, LLC · MEMORIES AT FULL RESOLUTION</span>
          </Legal>
        </FootInner>
      </Foot>
    </Page>
  )
}
